import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { fmtMoney, fmtErr } from "../api";
import Layout from "../components/Layout";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

const Card = ({ label, value, sub, testid }) => (
  <div className="card-flat p-5" data-testid={testid}>
    <p className="label-caps">{label}</p>
    <p className="font-heading text-3xl font-bold">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const StatusTable = ({ title, rows, testid }) => (
  <section data-testid={testid}>
    <p className="label-caps mb-2">{title}</p>
    <div className="card-flat divide-y divide-border">
      {rows.length === 0 && <p className="p-4 text-sm text-muted-foreground">None yet.</p>}
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between p-4 text-sm">
          <span className="font-semibold capitalize">{k.replaceAll("_", " ")}</span>
          <span className="font-bold">{v}</span>
        </div>
      ))}
    </div>
  </section>
);

export default function Platform() {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [storage, setStorage] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transferFrom, setTransferFrom] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferResult, setTransferResult] = useState("");

  const loadAccounts = () => api.get("/platform/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  const deleteTenant = async (a) => {
    if (!window.confirm(`Hard-delete ${a.email} and ALL their data? Properties, owners, crew, visits, jobs and messages are permanently removed. Invoices and payments are kept in the 6-year tax archive. This cannot be undone.`)) return;
    try { await api.post(`/platform/accounts/${a.id}/delete-tenant`); toast.success(`Deleted ${a.email}`); loadAccounts(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleActive = async (a) => {
    try {
      await api.post(`/platform/accounts/${a.id}/set-active`, { active: a.active === false });
      toast.success(a.active === false ? "Account reactivated" : "Account deactivated");
      loadAccounts();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doTransfer = async () => {
    if (!transferFrom.trim() || !transferTo.trim()) return toast.error("Enter both operator emails");
    try {
      const r = await api.post("/platform/transfer-tenant", { from_email: transferFrom, to_email: transferTo });
      const moved = r.data.moved;
      const total = Object.values(moved).reduce((s, n) => s + n, 0);
      setTransferResult(`Moved ${total} records (${Object.entries(moved).filter(([, n]) => n > 0).map(([k, n]) => `${k}: ${n}`).join(", ") || "nothing matched"})`);
      toast.success("Tenant data transferred");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  useEffect(() => {
    if (user?.platform_admin) {
      api.get("/platform/metrics").then((r) => setM(r.data)).catch((e) => toast.error(fmtErr(e)));
      api.get("/platform/storage-usage").then((r) => setStorage(r.data)).catch(() => {});
      loadAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadAccounts is stable
  }, [user]);

  if (user && !user.platform_admin) return <Navigate to="/" replace />;
  if (!m) return <Layout title="Platform"><p className="text-muted-foreground">Loading…</p></Layout>;

  const maxSignup = Math.max(1, ...m.signups_weekly.map((w) => w.count));

  return (
    <Layout title="Platform metrics">
      <p className="mb-6 rounded-md bg-muted p-3 text-xs text-muted-foreground" data-testid="platform-note">
        Veloktify-internal view — aggregate counts only. No tenant names, emails, addresses, or records are visible here.
      </p>
      <div className="space-y-8">
        <div className="grid grid-cols-2 gap-3" data-testid="platform-cards">
          <Card label="Operators" value={m.operators.total} sub={`${m.operators.active_30d} active in last 30 days`} testid="metric-operators" />
          <Card label="Owners" value={m.owners} testid="metric-owners" />
          <Card label="Properties" value={m.properties} testid="metric-properties" />
          <Card label="Subcontractors" value={m.subcontractors} testid="metric-subcontractors" />
          <Card label="Dispatchers" value={m.dispatchers} testid="metric-dispatchers" />
          <Card label="Visits" value={m.visits.total} sub={`${m.visits.completed_30d} completed in last 30 days`} testid="metric-visits" />
        </div>

        <section data-testid="storage-gauge">
          <p className="label-caps mb-2">Object storage — photos &amp; receipts</p>
          <div className="card-flat space-y-2 p-5">
            <div className="flex justify-between text-xs font-bold">
              <span data-testid="storage-used">{storage ? `${(storage.used_bytes / 1e9).toFixed(2)} GB used` : "…"}</span>
              <span data-testid="storage-pct">{storage ? `${storage.used_pct}% of 5 GB` : ""}</span>
            </div>
            <div className="h-2 rounded-full bg-border">
              <div className={`h-2 rounded-full transition-[width] ${(storage?.used_pct || 0) >= 80 ? "bg-destructive" : "bg-primary"}`}
                style={{ width: `${Math.min(100, storage?.used_pct || 0)}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">
              {storage ? `${storage.file_count} tracked uploads across all tenants` : "…"}
              {(storage?.used_pct || 0) >= 80 && <span className="font-bold text-destructive"> — approaching the 5GB quota; uploads start failing at 100%</span>}
            </p>
          </div>
        </section>

        <section data-testid="signup-series">
          <p className="label-caps mb-2">New operators per week — last 12 weeks</p>
          <div className="card-flat space-y-2 p-5">
            {m.signups_weekly.map((w) => (
              <div key={w.week} className="flex items-center gap-3" data-testid={`signup-week-${w.week}`}>
                <span className="w-20 shrink-0 text-xs text-muted-foreground">{w.week}</span>
                <div className="h-6 rounded bg-primary transition-all" style={{ width: `${(w.count / maxSignup) * 100}%`, minWidth: w.count ? 8 : 2, opacity: w.count ? 1 : 0.2 }} />
                <span className="text-xs font-bold">{w.count}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="grid gap-8 sm:grid-cols-1">
          <StatusTable title="Operators by plan" testid="plan-table"
            rows={Object.entries(m.operators_by_plan).map(([k, v]) => [k, v])} />
          <StatusTable title="Invoices by status (count · value)" testid="invoice-table"
            rows={Object.entries(m.invoices.by_status).map(([k, v]) => [k, `${v.count} · ${fmtMoney(v.value)}`])} />
          <StatusTable title="Observations by status" testid="observation-table"
            rows={Object.entries(m.observations.by_status).map(([k, v]) => [k, v])} />
          <StatusTable title="Jobs by status" testid="job-table"
            rows={Object.entries(m.jobs.by_status).map(([k, v]) => [k, v])} />
        </div>
        <section data-testid="support-tools">
          <p className="label-caps mb-2">Support tools</p>
          <div className="card-flat space-y-3 p-5">
            <p className="text-sm font-semibold">Transfer tenant data between operator accounts</p>
            <p className="text-xs text-muted-foreground">Moves every property, owner, crew member, agreement, visit, invoice, issue, job and settings from one operator account to another. Use when a customer needs a fresh login or an account is being retired.</p>
            <input data-testid="transfer-from" className="field-input w-full" placeholder="From operator email (source)" value={transferFrom} onChange={(e) => setTransferFrom(e.target.value)} />
            <input data-testid="transfer-to" className="field-input w-full" placeholder="To operator email (destination)" value={transferTo} onChange={(e) => setTransferTo(e.target.value)} />
            <button data-testid="transfer-btn" onClick={doTransfer} className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Transfer all tenant data</button>
            {transferResult && <p className="text-xs text-muted-foreground" data-testid="transfer-result">{transferResult}</p>}
          </div>
          <div className="card-flat mt-3 divide-y divide-border" data-testid="accounts-list">
            {accounts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-2 p-4 text-sm" data-testid={`account-${a.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{a.name} <span className="text-xs font-normal text-muted-foreground">{a.email}</span></p>
                  <p className="text-xs capitalize text-muted-foreground">{a.role}{a.subscription_status ? ` · ${a.subscription_status}` : ""}{a.active === false ? " · deactivated" : ""}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button data-testid={`account-toggle-${a.id}`} onClick={() => toggleActive(a)}
                    className={`h-9 rounded-md border px-3 text-xs font-bold ${a.active === false ? "border-primary text-primary" : "border-destructive text-destructive"}`}>
                    {a.active === false ? "Reactivate" : "Deactivate"}
                  </button>
                  {a.role === "operator" && !a.platform_admin && (
                    <button data-testid={`account-delete-${a.id}`} onClick={() => deleteTenant(a)}
                      title="Permanently delete this operator and all their data (invoices archived for tax)"
                      className="h-9 rounded-md bg-destructive px-3 text-xs font-bold text-destructive-foreground">Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}
